import { WbaEvents } from "./wba-events";
import type { TWbaEventCall, TWbaFunction } from "./wba-types";

type TWbaGlobal = typeof globalThis & { wba?: TWbaFunction };

type TQueuedCall = {
  call: TWbaEventCall;
  oneTime: boolean;
};

const sentOnce = new Set<string>();
const queue: TQueuedCall[] = [];
const POLL_MS = 50;
const MAX_WAIT_MS = 30_000;

let isWaiting = false;
let isInited = false;

const getWbaFn = (): TWbaFunction | undefined => {
  const wbaRaw = (globalThis as TWbaGlobal).wba;

  return typeof wbaRaw === "function" ? wbaRaw : undefined;
};

const sendToWba = (call: TWbaEventCall, oneTime: boolean) => {
  const key = `event:${call[0]}`;

  if (oneTime && sentOnce.has(key)) {
    return;
  }

  if (oneTime) {
    sentOnce.add(key);
  }

  getWbaFn()?.(...call);
};

export const flushWbaQueue = () => {
  if (queue.length === 0) {
    return;
  }

  const wbaFn = getWbaFn();

  if (!wbaFn || !isInited) {
    return;
  }

  const initIndex = queue.findIndex((item) => item.call[0] === WbaEvents.INIT);

  if (initIndex !== -1) {
    const initItem = queue.splice(initIndex, 1)[0];

    if (initItem) {
      sendToWba(initItem.call, initItem.oneTime);
    }
  }

  while (queue.length > 0) {
    const item = queue.shift();

    if (item) {
      sendToWba(item.call, item.oneTime);
    }
  }
};

export const wbaSendEvent = (call: TWbaEventCall, oneTime = false) => {
  const key = `event:${call[0]}`;

  if (oneTime && sentOnce.has(key)) {
    return;
  }

  if (call[0] === WbaEvents.INIT) {
    isInited = true;
  }

  const wbaFn = getWbaFn();

  if (wbaFn && isInited) {
    sendToWba(call, oneTime);

    return;
  }

  queue.push({ call, oneTime });

  if (isWaiting) {
    return;
  }

  isWaiting = true;
  const startedAt = Date.now();

  const poll = () => {
    if (getWbaFn() && isInited) {
      flushWbaQueue();
      isWaiting = false;

      return;
    }

    if (Date.now() - startedAt >= MAX_WAIT_MS) {
      if (queue.length > 0) {
        console.warn(
          `WBA: SDK not available after ${MAX_WAIT_MS}ms, ${queue.length} event(s) dropped`,
          queue.map((item) => item.call[0]),
        );
        queue.length = 0;
      }

      isWaiting = false;

      return;
    }

    setTimeout(poll, POLL_MS);
  };

  setTimeout(poll, POLL_MS);
};
